import { useMemo } from "react";

/**
 * Password validation requirements
 */
export interface PasswordRequirements {
  /** Minimum 8 characters */
  minLength: boolean;
  /** Contains at least one number */
  hasNumber: boolean;
  /** Contains at least one symbol */
  hasSymbol: boolean;
  /** Contains at least one uppercase letter */
  hasUppercase: boolean;
  /** Contains at least one lowercase letter */
  hasLowercase: boolean;
}

/**
 * Result of password validation hook
 */
export interface UsePasswordValidationResult {
  /** Individual requirement checks */
  requirements: PasswordRequirements;
  /** Whether all requirements are met */
  isValid: boolean;
  /** List of unmet requirements */
  errors: string[];
  /** Strength score from 0-5 */
  strength: number;
}

/**
 * Error messages for each requirement
 */
const ERROR_MESSAGES = {
  minLength: "passwordMin8",
  hasNumber: "passwordNeedNumber",
  hasSymbol: "passwordNeedSymbol",
  hasUppercase: "passwordNeedUpper",
  hasLowercase: "passwordNeedLower",
} as const;

/**
 * Custom hook for validating password strength and requirements
 *
 * Provides real-time validation feedback for password inputs,
 * checking against common security requirements.
 *
 * @param password - The password string to validate
 * @returns Validation result with requirements, validity, errors, and strength
 *
 * @example
 * ```tsx
 * const { requirements, isValid, errors, strength } = usePasswordValidation(password);
 *
 * // Display requirement checklist
 * <ul>
 *   {Object.entries(requirements).map(([key, met]) => (
 *     <li key={key} className={met ? 'text-green-500' : 'text-red-500'}>
 *       {labelsMap[key]}
 *     </li>
 *   ))}
 * </ul>
 * ```
 */
export const usePasswordValidation = (
  password: string,
): UsePasswordValidationResult => {
  return useMemo(() => {
    const requirements: PasswordRequirements = {
      minLength: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasSymbol: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
    };

    const errors: string[] = [];

    (Object.keys(requirements) as (keyof PasswordRequirements)[]).forEach(
      (key) => {
        if (!requirements[key]) {
          errors.push(ERROR_MESSAGES[key]);
        }
      },
    );

    const strength = Object.values(requirements).filter(Boolean).length;
    const isValid = strength === 5;

    return {
      requirements,
      isValid,
      errors,
      strength,
    };
  }, [password]);
};
